/**
 * Feature E02-F02-T05 — Client autocomplete from the DB.
 *
 * 1:1 port of the legacy `../EER app/public/assets/js/project.js`
 * `renderClientAutocomplete`: a free-text input over the `state.clients` cache
 * (GET /clients) with a filtered dropdown.
 *
 * Behaviour (acceptance, all rules in `projectDetailModel.ts` so they are
 * unit-tested DOM-free):
 *   - typing filters the dropdown by case-insensitive SUBSTRING (unbounded);
 *   - an EMPTY input shows the first 10 cached clients (the cache is name-sorted);
 *   - an "Add new: <typed>" row appears when no client matches exactly →
 *     selecting it stages `client_id = null` (create a new client on save);
 *   - the CRITICAL rename edge — typing a name that no longer matches the linked
 *     client clears `client_id` to null (a NEW client is created), handled in
 *     `resolveClientName`, applied on every keystroke via `onResolve`;
 *   - clicking OUTSIDE the control closes the dropdown without committing.
 *
 * The component is controlled: the parent owns the typed `value` and receives a
 * `{client_name, client_id}` resolution on each change / selection, which it
 * stages into `unsavedUpdates` (deferred save — parity with the legacy header
 * inline edit).
 */
import { useEffect, useRef, useState, type JSX } from 'react';
import type { ClientOption } from '@eer/shared';
import {
  addNewLabel,
  filterClientOptions,
  resolveClientName,
  shouldOfferAddNew,
  type ClientResolution,
} from '../shared/clientAutocompleteModel.js';

export interface ClientAutocompleteProps {
  /** The current typed client name (controlled). */
  readonly value: string;
  /** The full clients cache (GET /clients). */
  readonly clients: readonly ClientOption[];
  /**
   * Fired on every change / selection with the resolved `{client_name,
   * client_id}` — client_id is a number on an exact match, `null` to force a new
   * client (Add-new OR a rename that no longer matches). The parent stages this.
   */
  readonly onResolve: (resolution: ClientResolution) => void;
  /** Placeholder text. */
  readonly placeholder?: string;
  /** Test-id prefix for the input + dropdown options. */
  readonly testId?: string;
  /** Disable the field (e.g. while saving). */
  readonly disabled?: boolean;
  /**
   * Override the input's classes (the header styles the Client field as the HERO
   * — 2xl gold serif — while other consumers keep the default bordered input).
   */
  readonly inputClassName?: string;
  /**
   * Whether to offer the "Add new: <typed>" row (default true — the select-or-
   * create behaviour). A PURE filter (e.g. the Dashboard Client facet, E02-F01-T10)
   * passes `false`: there is nothing to create, so only existing clients are
   * offered and free text just narrows.
   */
  readonly allowAddNew?: boolean;
  /**
   * Accessible name for the input (default 'Client'). A non-client reuse — e.g.
   * the Dashboard Position facet driven by the same control — overrides this so
   * screen readers announce the correct field.
   */
  readonly ariaLabel?: string;
}

export function ClientAutocomplete({
  value,
  clients,
  onResolve,
  placeholder = 'Search or add a client…',
  testId = 'client-autocomplete',
  disabled = false,
  inputClassName,
  allowAddNew = true,
  ariaLabel = 'Client',
}: ClientAutocompleteProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on an outside click (legacy: clicking elsewhere dismisses the dropdown
  // without committing the highlighted option).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const options = filterClientOptions(clients, value);
  const offerAddNew = allowAddNew && shouldOfferAddNew(clients, value);

  /** Commit a typed value (every keystroke re-resolves — incl. the rename edge). */
  function handleChange(next: string): void {
    onResolve(resolveClientName(clients, next));
    setOpen(true);
  }

  /** Pick a cached client → link to its id. */
  function pick(client: ClientOption): void {
    onResolve({ client_name: client.name, client_id: client.id });
    setOpen(false);
  }

  /** Pick "Add new" → keep the typed name, force client_id = null. */
  function pickAddNew(): void {
    onResolve({ client_name: value.trim(), client_id: null });
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        data-testid={`${testId}-input`}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setOpen(true)}
        className={
          inputClassName ??
          'w-full border rounded px-2 py-1.5 text-sm focus:border-[#D4AF37] outline-none disabled:bg-gray-50 disabled:text-gray-400'
        }
      />
      {open && (options.length > 0 || offerAddNew) ? (
        <ul
          data-testid={`${testId}-dropdown`}
          className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg"
        >
          {options.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                data-testid={`${testId}-option`}
                data-client-id={client.id}
                onClick={() => pick(client)}
                className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {client.name}
              </button>
            </li>
          ))}
          {offerAddNew ? (
            <li>
              <button
                type="button"
                data-testid={`${testId}-add-new`}
                onClick={pickAddNew}
                className="block w-full border-t border-gray-100 px-3 py-1.5 text-left text-sm font-medium text-[#B5952F] hover:bg-amber-50"
              >
                {addNewLabel(value)}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export default ClientAutocomplete;
